import { DestroyRef, Injectable, WritableSignal, inject, signal, untracked } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { EMPTY, Observable, catchError, switchMap } from 'rxjs';
import { TestStepAttachment } from '../models/test-case.model';
import { TestStepService } from './test-step.service';

/**
 * Die Vorlage eines Testschritts: das erste Bild unter den Anhaengen des
 * Bibliotheks-Schritts, nachgeschlagen ueber seine {@code testStepId}.
 *
 * <p>Wann die Bytes kommen, entscheidet die Seite. Der Runner trennt die Stufen:
 * {@link discover} klaert fuer die ganze Liste, <em>ob</em> es eine Vorlage gibt, und
 * {@link load} holt sie nur fuer den angezeigten Schritt — ein Vielfaches an Daten fuer
 * ein Bild, das meistens niemand aufschlaegt, spart er sich so. Die Testlauf-Seite holt
 * mit {@link prefetch} beides im Voraus.
 *
 * <p>Beide Karten unterscheiden drei Zustaende: kein Eintrag heisst „noch nicht
 * gefragt", {@code null} heisst „gefragt, nichts da (oder noch unterwegs)". Ohne diese
 * Unterscheidung liefe jeder Wechsel in derselben Liste erneut ins Netz.
 *
 * <p>Bewusst nicht {@code providedIn: 'root'} — die Instanz gehoert der Seite, die sie
 * in ihre {@code providers} aufnimmt, und gibt ihre Object-URLs mit deren Ende wieder
 * frei. Eine app-weite Instanz haette keinen Zeitpunkt, an dem das gefahrlos waere.
 */
@Injectable()
export class StepReferenceImageService {
  private readonly testSteps = inject(TestStepService);
  private readonly destroyRef = inject(DestroyRef);

  private readonly attachmentIds = signal<Record<string, string | null>>({});
  private readonly objectUrls = signal<Record<string, string | null>>({});

  constructor() {
    this.destroyRef.onDestroy(() => {
      for (const url of Object.values(untracked(this.objectUrls))) {
        if (url !== null) URL.revokeObjectURL(url);
      }
    });
  }

  /** Ob der Schritt eine Vorlage traegt — eine Schrittliste zeigt daran ihr Symbol. */
  has(stepId: string | null | undefined): boolean {
    if (!stepId) return false;
    return this.attachmentIds()[stepId] != null;
  }

  /** Blob-URL der Vorlage, {@code null} solange es keine gibt. */
  url(stepId: string | null | undefined): string | null {
    if (!stepId) return null;
    return this.objectUrls()[stepId] ?? null;
  }

  /**
   * Fragt die Anhaenge des Schritts ab und merkt sich die Vorlage — einmal je Schritt,
   * ohne ihre Bytes zu holen. Ein freier Schritt ohne {@code testStepId} hat keine und
   * wird nicht gefragt.
   */
  discover(projectId: string, stepId: string | null | undefined): void {
    if (!stepId || !this.claim(this.attachmentIds, stepId)) return;
    this.silently(this.testSteps.listAttachments(projectId, stepId)).subscribe((attachments) =>
      this.rememberImage(stepId, attachments),
    );
  }

  /**
   * Holt die Bytes der von {@link discover} gefundenen Vorlage — einmal je Schritt.
   * Die Anhangs-Karte wird dabei mitverfolgt, denn sobald sie die Id nachtraegt, soll
   * ein aufrufender Effect erneut laufen.
   */
  load(projectId: string, stepId: string | null | undefined): void {
    if (!stepId) return;
    const attachmentId = this.attachmentIds()[stepId];
    if (!attachmentId || !this.claim(this.objectUrls, stepId)) return;
    this.silently(this.testSteps.downloadAttachment(projectId, stepId, attachmentId)).subscribe((blob) =>
      this.storeUrl(stepId, blob),
    );
  }

  /** Beide Stufen in einem Zug, fuer Seiten die ihre Vorlagen im Voraus holen. */
  prefetch(projectId: string, stepId: string | null | undefined): void {
    if (!stepId || !this.claim(this.attachmentIds, stepId)) return;
    this.silently(
      this.testSteps.listAttachments(projectId, stepId).pipe(
        switchMap((attachments) => {
          const image = this.rememberImage(stepId, attachments);
          if (!image || !this.claim(this.objectUrls, stepId)) return EMPTY;
          return this.testSteps.downloadAttachment(projectId, stepId, image.id);
        }),
      ),
    ).subscribe((blob) => this.storeUrl(stepId, blob));
  }

  /**
   * Meldet den Schritt als „gefragt" an und sagt, ob der Aufrufer ihn holen soll.
   * Der bekannte Stand wird {@code untracked} gelesen: ein Effect, der das hier
   * aufruft, stiesse sich sonst ueber die eigene Schreiboperation selbst wieder an.
   */
  private claim(map: WritableSignal<Record<string, string | null>>, stepId: string): boolean {
    if (untracked(map)[stepId] !== undefined) return false;
    map.update((known) => ({ ...known, [stepId]: null }));
    return true;
  }

  private rememberImage(stepId: string, attachments: TestStepAttachment[]): TestStepAttachment | null {
    const image = attachments.find((attachment) => attachment.contentType?.startsWith('image/')) ?? null;
    if (image) this.attachmentIds.update((map) => ({ ...map, [stepId]: image.id }));
    return image;
  }

  private storeUrl(stepId: string, blob: Blob): void {
    this.objectUrls.update((map) => ({ ...map, [stepId]: URL.createObjectURL(blob) }));
  }

  /**
   * Ein fehlgeschlagener Abruf bleibt ohne Folgen: kein Bild, kein Banner. Die Seite
   * laesst sich ohne Vorlage weiterhin bedienen, und eine Meldung ueber ein Bild, das
   * niemand vermisst, stuende dem im Weg.
   */
  private silently<T>(source: Observable<T>): Observable<T> {
    return source.pipe(
      catchError(() => EMPTY),
      takeUntilDestroyed(this.destroyRef),
    );
  }
}
